import { useState, useRef, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import styles from "./hiring.module.scss";
import back from "../icons/back.svg";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LfXDUstAAAAAHqPZ78hKBPgTe_fP-zYyBqIVV8X";

const API = process.env.REACT_APP_UDAAN_API || "https://api.prd.swz-infra.net/recommendation-service/api/career";

/* ─── static fallback (shown while loading) ──────────── */
const JOBS_FALLBACK = [
  {
    id: "architect",
    title: "Principal / Staff AI ERP Architect",
    type: "Full-time · Remote",
    exp: "8+ years",
    dept: "Engineering",
    skills: ["Python", "AWS", "LLMs", "System Design"],
    summary: "Own the target architecture for the agentic AI ERP platform, including migration strategy, service boundaries, data architecture, security, and reliability.",
    responsibilities: [
      "Design the end-state architecture for an AI-agent-based ERP platform.",
      "Define agent orchestration, human-in-the-loop controls, audit trails, and system observability.",
      "Guide migration from the existing PHP monolith to modern Python/AWS services.",
      "Set engineering standards for APIs, data access, privacy, security, and deployment.",
      "Review critical technical decisions across AI, ERP, telephony, database, and integration layers.",
    ],
    required: [
      "Strong Python, API, system design, and cloud architecture skills.",
      "Hands-on understanding of AWS, databases, LLMs, RAG, AI agents, and automation flows.",
      "MongoDB or document-database experience.",
      "Ability to work with legacy PHP systems during migration.",
      "Strong understanding of DPDPA compliance, consent, access control, retention, and auditability.",
    ],
    niceToHave: [
      "ERP implementation or modernization experience.",
      "Experience with voice AI, telephony, or contact-center integrations.",
      "Experience in regulated or privacy-sensitive enterprise systems.",
    ],
  },
  {
    id: "eng-manager",
    title: "Engineering Manager / Technical Delivery Lead",
    type: "Full-time · Remote",
    exp: "5+ years",
    dept: "Engineering",
    skills: ["Team Leadership", "Python", "AWS", "Delivery Management"],
    summary: "Lead execution for the remote engineering team and keep delivery, quality, compliance, and stakeholder alignment on track.",
    responsibilities: [
      "Manage sprint planning, delivery tracking, engineering coordination, and release readiness.",
      "Work with product, business, and engineering teams to break ERP transformation work into milestones.",
      "Maintain delivery discipline across architecture, development, QA, documentation, and deployment.",
      "Ensure the team follows secure, DPDPA-compliant engineering practices.",
      "Support hiring, onboarding, mentoring, and performance management for a remote team.",
    ],
    required: [
      "Experience leading software teams in fast-moving environments.",
      "Working understanding of Python, AWS, APIs, databases, and AI/LLM-based systems.",
      "Strong communication, prioritization, and delivery management skills.",
      "Ability to handle ambiguity and drive decisions with incomplete information.",
      "Practical understanding of DPDPA compliance and privacy-aware delivery.",
    ],
    niceToHave: [
      "ERP, SaaS, or enterprise automation experience.",
      "Experience managing remote engineering teams.",
      "Familiarity with PHP-based legacy systems.",
    ],
  },
  {
    id: "fullstack",
    title: "Senior AI Full-Stack Engineer",
    type: "Full-time · Remote",
    exp: "4+ years",
    dept: "Engineering",
    openings: 2,
    skills: ["Python", "LLMs", "MongoDB", "AWS"],
    summary: "Build agentic ERP workflows, backend services, integrations, and user-facing tools using Python, AWS, MongoDB, and LLMs.",
    responsibilities: [
      "Develop AI-agent workflows for ERP modules and stakeholder interactions.",
      "Build Python services, APIs, dashboards, and backend integrations.",
      "Connect LLM workflows with ERP data, MongoDB, external systems, and telephony services.",
      "Implement logging, monitoring, guardrails, and human approval flows.",
      "Collaborate with QA and analysts to validate AI behavior and business rules.",
    ],
    required: [
      "Strong Python programming skills.",
      "Experience with LLMs, prompt workflows, RAG, tool calling, or AI agent orchestration.",
      "Working knowledge of AWS and MongoDB.",
      "Comfort with APIs, databases, and production debugging.",
      "DPDPA-aware approach to personal data, transcripts, financial records, and access controls.",
    ],
    niceToHave: [
      "ERP experience or business workflow automation experience.",
      "PHP familiarity for legacy system migration.",
      "Experience with telephony, voice AI, or conversational interfaces.",
    ],
  },
  {
    id: "php-erp",
    title: "Senior PHP / ERP Migration Engineer",
    type: "Full-time · Remote",
    exp: "5+ years",
    dept: "Engineering",
    skills: ["PHP", "SQL", "Python", "ERP Systems"],
    summary: "Understand the existing PHP ERP monolith deeply and help migrate its modules, workflows, and data safely into the new AI-first platform.",
    responsibilities: [
      "Analyze the existing PHP ERP codebase, database schema, integrations, and business rules.",
      "Document module behavior and support phased migration to Python/AWS services.",
      "Build compatibility APIs, migration utilities, and validation checks.",
      "Partner with AI engineers to expose ERP actions safely to agent workflows.",
      "Preserve data integrity, auditability, and business continuity during migration.",
    ],
    required: [
      "Strong PHP and backend engineering experience.",
      "Experience with SQL databases, APIs, integrations, and legacy application maintenance.",
      "Ability to read complex existing code and extract business logic.",
      "Familiarity with Python, AWS, and MongoDB or willingness to ramp quickly.",
      "Understanding of DPDPA implications for legacy data migration and access control.",
    ],
    niceToHave: [
      "ERP experience in finance, inventory, procurement, CRM, HRMS, or operations modules.",
      "Experience decomposing monoliths into services.",
      "Experience with data migration and reconciliation.",
    ],
  },
  {
    id: "data-eng",
    title: "Senior Data / Database Engineer",
    type: "Full-time · Remote",
    exp: "4+ years",
    dept: "Engineering",
    skills: ["MongoDB", "Data Modeling", "Python", "AWS"],
    summary: "Own data modeling, migration validation, reporting datasets, audit trails, and privacy-safe data access for the AI ERP platform.",
    responsibilities: [
      "Design MongoDB collections, indexes, data access patterns, and migration strategies.",
      "Map legacy ERP data into the new system with reconciliation and validation checks.",
      "Build data pipelines for operational reporting, analytics, and AI retrieval workflows.",
      "Implement retention, masking, access controls, and audit-friendly data practices.",
      "Support QA in validating data correctness across ERP modules.",
    ],
    required: [
      "Strong database design and data migration experience.",
      "Hands-on MongoDB experience or strong document-database fundamentals.",
      "Python scripting and data validation skills.",
      "Working knowledge of AWS data services.",
      "Strong understanding of DPDPA compliance for storage, processing, access, and retention.",
    ],
    niceToHave: [
      "ERP database or reporting experience.",
      "Experience with RAG data pipelines and vector search.",
      "Experience with financial, employee, customer, or vendor data.",
    ],
  },
  {
    id: "telephony",
    title: "Senior Telephony / Voice Systems Engineer",
    type: "Full-time · Remote",
    exp: "4+ years",
    dept: "Engineering",
    skills: ["VoIP", "SIP", "IVR", "Python"],
    summary: "Build telephony and voice-agent integrations that allow AI ERP workflows to interact with stakeholders through calls, transcripts, and escalations.",
    responsibilities: [
      "Design and implement telephony integrations for AI-agent ERP workflows.",
      "Work on call routing, IVR, transcription, voice bots, escalation, and call logging.",
      "Integrate voice systems with ERP modules, APIs, databases, and AI services.",
      "Ensure secure handling of recordings, transcripts, caller data, and consent flows.",
      "Collaborate with AI engineers on latency, reliability, and human fallback behavior.",
    ],
    required: [
      "Experience with SIP, VoIP, IVR, call routing, or contact-center platforms.",
      "API integration experience and working knowledge of Python or similar backend language.",
      "Familiarity with AWS and databases.",
      "Ability to learn AI/LLM workflows quickly.",
      "DPDPA knowledge for consent, call recording, transcript storage, and retention.",
    ],
    niceToHave: [
      "Experience with Twilio, Exotel, Asterisk, FreePBX, Amazon Connect, or similar tools.",
      "Experience with speech-to-text, text-to-speech, and voice AI agents.",
      "ERP or CRM integration experience.",
    ],
  },
  {
    id: "pm",
    title: "Product / Project Manager – AI ERP Transformation",
    type: "Full-time · Remote",
    exp: "3+ years",
    dept: "Product",
    skills: ["Product Management", "Stakeholder Coordination", "Documentation", "DPDPA"],
    summary: "Own requirements, prioritization, stakeholder coordination, documentation, and delivery visibility for the AI ERP transformation.",
    responsibilities: [
      "Gather requirements and translate ERP workflows into clear product specifications.",
      "Prioritize modules, automations, AI-agent flows, and releases.",
      "Coordinate stakeholders, engineers, analysts, QA, and telephony teams.",
      "Maintain product backlog, milestones, risk log, release notes, and acceptance criteria.",
      "Ensure requirements include DPDPA-aware consent, access, retention, and audit needs.",
    ],
    required: [
      "Strong requirement gathering, documentation, prioritization, and execution tracking skills.",
      "Ability to work with technical teams on APIs, databases, cloud, and AI workflows.",
      "Excellent stakeholder communication and remote collaboration skills.",
      "Fast learner with high adaptability.",
      "Working knowledge of DPDPA compliance and privacy-by-design practices.",
    ],
    niceToHave: [
      "ERP implementation or business process consulting experience.",
      "MBA or operations background.",
      "Familiarity with Python, AWS, MongoDB, or LLM concepts.",
    ],
  },
  {
    id: "ba",
    title: "ERP Business Analyst",
    type: "Full-time · Remote",
    exp: "1–3 years",
    dept: "Business",
    openings: 2,
    skills: ["Business Analysis", "Process Mapping", "Documentation", "ERP"],
    summary: "Document current ERP workflows, define future AI-agent workflows, and support requirement validation, test cases, and stakeholder alignment.",
    responsibilities: [
      "Study existing ERP workflows and document current business processes.",
      "Prepare process maps, requirement documents, user stories, test cases, and acceptance criteria.",
      "Support module prioritization, data mapping, and user validation.",
      "Coordinate with engineering and product teams to clarify business rules.",
      "Identify DPDPA-sensitive data points in workflows and document privacy requirements.",
    ],
    required: [
      "Strong analytical, documentation, and communication skills.",
      "Ability to understand business processes and translate them into requirements.",
      "Basic understanding of databases, APIs, automation, or software systems.",
      "Fast learner with strong adaptability.",
      "Awareness of DPDPA compliance and privacy-sensitive workflow design.",
    ],
    niceToHave: [
      "ERP exposure in finance, inventory, procurement, CRM, HRMS, or operations.",
      "MBA, business operations, or consulting background.",
      "Exposure to AI tools, LLMs, or workflow automation.",
    ],
  },
  {
    id: "qa",
    title: "QA Automation Engineer – AI ERP Platform",
    type: "Full-time · Remote",
    exp: "2+ years",
    dept: "Engineering",
    skills: ["Test Automation", "API Testing", "Python", "QA"],
    summary: "Create automated and manual test coverage for ERP modules, AI-agent workflows, APIs, data validation, permissions, and telephony interactions.",
    responsibilities: [
      "Build test plans for ERP workflows, AI outputs, APIs, UI flows, and telephony scenarios.",
      "Create automated regression tests for critical business workflows.",
      "Validate data accuracy, permissions, audit logs, and business rules.",
      "Test AI-agent behavior using structured evaluation datasets and expected outcomes.",
      "Verify DPDPA-sensitive workflows such as consent, data access, deletion, and retention.",
    ],
    required: [
      "Experience in software testing, test case design, and defect reporting.",
      "Familiarity with API testing, database validation, and automation tools.",
      "Basic Python or scripting knowledge.",
      "Strong attention to detail and fast learning ability.",
      "Understanding of privacy, security, and DPDPA compliance testing.",
    ],
    niceToHave: [
      "ERP testing experience.",
      "Experience with Playwright, Selenium, Postman, PyTest, or similar tools.",
      "Experience testing AI agents, chatbots, or automation workflows.",
    ],
  },
  {
    id: "intern",
    title: "AI ERP Transformation Intern",
    type: "Full-time · Remote",
    exp: "0–1 year",
    dept: "Intern",
    openings: 4,
    skills: ["Python (basic)", "Documentation", "Research", "Fast Learner"],
    summary: "Support documentation, testing, research, workflow mapping, data organization, and basic automation tasks for the AI ERP transformation.",
    responsibilities: [
      "Assist with ERP process documentation, meeting notes, and workflow mapping.",
      "Help prepare test cases, prompt test sets, user feedback summaries, and data checks.",
      "Support basic Python scripts, dashboards, or automation tasks under supervision.",
      "Research ERP modules, AI-agent patterns, AWS services, MongoDB concepts, and DPDPA requirements.",
      "Follow strict confidentiality and privacy practices when handling project information.",
    ],
    required: [
      "Basic understanding of Python or software systems.",
      "Strong willingness to learn and adapt quickly.",
      "Good written communication and documentation skills.",
      "Ability to work in a fast-moving remote team.",
      "Awareness of data privacy and willingness to follow DPDPA practices.",
    ],
    niceToHave: [
      "Exposure to AI tools, LLMs, automation, or databases.",
      "MBA, engineering, computer science, or business operations background.",
      "Interest in ERP, SaaS, enterprise automation, or AI products.",
    ],
  },
];

/* ─── Apply modal now POSTs to API ───────────────────── */

const DEPT_COLORS = {
  Engineering: { bg: "rgba(31,111,235,0.15)", color: "#58a6ff" },
  Product:     { bg: "rgba(124,58,237,0.15)", color: "#a78bfa" },
  Business:    { bg: "rgba(16,185,129,0.15)", color: "#34d399" },
  Intern:      { bg: "rgba(245,158,11,0.15)", color: "#fbbf24" },
};

function JobCard({ job, onApply }) {
  const [expanded, setExpanded] = useState(false);
  const deptStyle = DEPT_COLORS[job.dept] || DEPT_COLORS.Engineering;

  return (
    <div className={styles.card}>
      <div className={styles.card__row}>
        <div className={styles.card__left}>
          <div className={styles.card__badges}>
            <span className={styles.card__dept} style={{ background: deptStyle.bg, color: deptStyle.color }}>
              {job.dept}
            </span>
            <span className={job.isOpen === false ? styles.card__closed : styles.card__open}>
              {job.isOpen === false ? "● Closed" : "● Open"}
            </span>
          </div>
          <h3 className={styles.card__title}>{job.title}</h3>
          <div className={styles.card__meta}>
            <span>{job.type}</span>
            <span className={styles.card__dot}>·</span>
            <span>{job.exp}</span>
            {job.openings && (
              <>
                <span className={styles.card__dot}>·</span>
                <span>{job.openings} {job.openings === 1 ? "opening" : "openings"}</span>
              </>
            )}
          </div>
          <div className={styles.card__skills}>
            {job.skills.map(s => (
              <span key={s} className={styles.card__skill}>{s}</span>
            ))}
          </div>
        </div>
        <div className={styles.card__right}>
          <button className={styles.card__jd__btn} onClick={() => setExpanded(e => !e)}>
            {expanded ? "Hide JD ↑" : "View JD ↓"}
          </button>
          <button className={styles.card__apply__btn} onClick={() => onApply(job)}>
            Apply Now →
          </button>
        </div>
      </div>

      {expanded && (
        <div className={styles.card__jd}>
          <div className={styles.card__jd__cols}>
            <div className={styles.card__jd__section}>
              <h4>Responsibilities</h4>
              <ul>{job.responsibilities.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
            <div className={styles.card__jd__section}>
              <h4>Required Skills</h4>
              <ul>{job.required.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
            <div className={styles.card__jd__section}>
              <h4>Nice to Have</h4>
              <ul>{job.niceToHave.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ApplyModal({ job, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", exp: "", linkedin: "", message: "" });
  const [resumeFile, setResumeFile] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (file) setResumeFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) setResumeFile(file);
  };

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const captchaRef = useRef(null);
  const pendingFormData = useRef(null);

  const doSubmit = async (token) => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const fd = pendingFormData.current;
      fd.append("captcha_token", token);
      const res = await fetch(`${API}/apply`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        const msg = typeof detail === "string" ? detail
          : Array.isArray(detail) ? detail[0]?.msg || "Validation error"
          : data.message || "Something went wrong";
        throw new Error(msg);
      }
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || "Something went wrong, please try again.");
      captchaRef.current?.reset();
    } finally {
      setSubmitting(false);
      pendingFormData.current = null;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("job_id", job._id || job.id);
    fd.append("name", form.name);
    fd.append("email", form.email);
    fd.append("phone", form.phone);
    fd.append("exp", form.exp);
    fd.append("linkedin", form.linkedin);
    fd.append("message", form.message);
    if (resumeFile) fd.append("resume", resumeFile);
    pendingFormData.current = fd;
    captchaRef.current.execute();
  };

  const needsResume = !resumeFile && !form.linkedin;

  return (
    <div className={styles.modal__overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <button className={styles.modal__close} onClick={onClose}>✕</button>
        {submitted ? (
          <div className={styles.modal__success}>
            <div className={styles.modal__success__icon}>🎉</div>
            <h2>Application Sent!</h2>
            <p>We'll review your profile and get back to you soon.</p>
            <button className={styles.modal__submit} onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <div className={styles.modal__badge}>Applying for</div>
            <h2 className={styles.modal__title}>{job.title}</h2>
            <p className={styles.modal__sub}>{job.type} · {job.exp} experience</p>
            <form className={styles.modal__form} onSubmit={handleSubmit}>
              <div className={styles.modal__row}>
                <div className={styles.modal__field}>
                  <label>Full Name *</label>
                  <input required placeholder="Your name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div className={styles.modal__field}>
                  <label>Email *</label>
                  <input required type="email" placeholder="you@email.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                </div>
              </div>
              <div className={styles.modal__row}>
                <div className={styles.modal__field}>
                  <label>Phone *</label>
                  <input required placeholder="+91 00000 00000" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                </div>
                <div className={styles.modal__field}>
                  <label>Years of Experience *</label>
                  <input required type="number" placeholder="e.g. 4" value={form.exp} onChange={e => setForm(f => ({ ...f, exp: e.target.value }))} />
                </div>
              </div>

              {/* Resume / LinkedIn */}
              <div className={styles.modal__field}>
                <label>Resume / LinkedIn *</label>
                <div className={styles.modal__resume}>
                  {/* File upload zone */}
                  <div
                    className={`${styles.modal__dropzone} ${resumeFile ? styles.modal__dropzone__filled : ""}`}
                    onClick={() => fileRef.current.click()}
                    onDrop={handleDrop}
                    onDragOver={e => e.preventDefault()}
                  >
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".pdf,.doc,.docx"
                      style={{ display: "none" }}
                      onChange={handleFile}
                    />
                    {resumeFile ? (
                      <>
                        <span className={styles.modal__file__icon}>📄</span>
                        <span className={styles.modal__file__name}>{resumeFile.name}</span>
                        <button
                          type="button"
                          className={styles.modal__file__remove}
                          onClick={e => { e.stopPropagation(); setResumeFile(null); }}
                        >✕</button>
                      </>
                    ) : (
                      <>
                        <span className={styles.modal__dropzone__icon}>↑</span>
                        <span className={styles.modal__dropzone__text}>Drop resume here or <u>browse</u></span>
                        <span className={styles.modal__dropzone__hint}>PDF, DOC, DOCX</span>
                      </>
                    )}
                  </div>

                  <div className={styles.modal__or}>
                    <span>or paste LinkedIn URL</span>
                  </div>

                  <input
                    placeholder="https://linkedin.com/in/..."
                    value={form.linkedin}
                    onChange={e => setForm(f => ({ ...f, linkedin: e.target.value }))}
                    disabled={!!resumeFile}
                    className={resumeFile ? styles.modal__input__disabled : ""}
                  />
                </div>
                {needsResume && <span className={styles.modal__hint}>Upload a file or paste LinkedIn URL</span>}
              </div>

              <div className={styles.modal__field}>
                <label>Why are you interested?</label>
                <textarea rows={3} placeholder="Tell us a bit about yourself..." value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} />
              </div>
              <ReCAPTCHA
                ref={captchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                size="invisible"
                onChange={token => { if (token) doSubmit(token); }}
              />
              {submitError && <p style={{ color: "#f85149", fontSize: 13, margin: 0 }}>{submitError}</p>}
              <button type="submit" className={styles.modal__submit} disabled={needsResume || submitting}>
                {submitting ? "Sending..." : "Send Application →"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const DEPTS = ["All", "Engineering", "Product", "Business", "Intern"];

export default function Hiring() {
  const [activeDept, setActiveDept] = useState("All");
  const [applyJob, setApplyJob] = useState(null);
  const [jobs, setJobs] = useState(JOBS_FALLBACK);

  useEffect(() => {
    fetch(`${API}/jobs`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data) && data.length > 0) setJobs(data); })
      .catch(() => {});
  }, []);

  const filtered = activeDept === "All" ? jobs : jobs.filter(j => j.dept === activeDept);

  return (
    <div className={styles.career}>
      <div className={styles.ring1} />
      <div className={styles.ring2} />
      <div className={styles.aurora1} />
      <div className={styles.aurora2} />

      <header className={styles.header}>
        <div className={styles.header__logo}>
          <img src="/logoname.png" alt="Sowiz" />
        </div>
      </header>

      <div className={styles.career__head} onClick={() => { window.location.href = "/"; }}>
        <img src={back} alt="Back" />
        <span>Back</span>
      </div>

      <div className={styles.career__hero}>
        <p className={styles.career__hero__tag}>Come Join Us</p>
        <h1 className={styles.career__hero__title}>Career Openings</h1>
        <p className={styles.career__hero__desc}>
          We're building the future of AI-driven ERP. Join a remote-first team that moves fast, thinks big, and ships real things.
        </p>
        <div className={styles.career__stats}>
          <div className={styles.career__stat}>
            <span>{jobs.length}</span>
            <p>Open Roles</p>
          </div>
          <div className={styles.career__stat__divider} />
          <div className={styles.career__stat}>
            <span>100%</span>
            <p>Remote</p>
          </div>
          <div className={styles.career__stat__divider} />
          <div className={styles.career__stat}>
            <span>{[...new Set(jobs.map(j => j.dept))].length}</span>
            <p>Departments</p>
          </div>
        </div>
      </div>

      <div className={styles.career__body}>
        <div className={styles.career__filters}>
          {DEPTS.map(d => (
            <button
              key={d}
              className={`${styles.filter__btn} ${activeDept === d ? styles.filter__btn__active : ""}`}
              onClick={() => setActiveDept(d)}
            >
              {d}
            </button>
          ))}
        </div>

        <div className={styles.career__grid}>
          {filtered.map(job => (
            <JobCard key={job.id} job={job} onApply={setApplyJob} />
          ))}
        </div>

        <div className={styles.career__cta}>
          <h3>Don't see your role?</h3>
          <p>We're always open to exceptional talent. Drop us a line.</p>
          <a href="mailto:careers@sowiz.live" className={styles.career__cta__btn}>
            Send Us Your Profile →
          </a>
        </div>
      </div>

      {applyJob && <ApplyModal job={applyJob} onClose={() => setApplyJob(null)} />}
    </div>
  );
}
